/* global JSX */
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CircleIcon from "@mui/icons-material/Circle";
import InboxIcon from "@mui/icons-material/Inbox";
import MessageIcon from "@mui/icons-material/Message";
import MoveToInboxIcon from "@mui/icons-material/MoveToInbox";
import SmsIcon from "@mui/icons-material/Sms";
import WatchLaterIcon from "@mui/icons-material/WatchLater";
import { Box, Chip, Grid, Stack, Tooltip } from "@mui/material";
import * as React from "react";
import { ToolButtonDropdown } from "ui/Scripts/source/components/framework/Button/ToolButtonDropdown";
import { Base } from "ui/Scripts/source/framework/base";
import { IIdTitleNode } from "ui/Scripts/source/models/common/idTitleNode";
import { EnumHelper, WhistleBlowState } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { IWhistleBlowListItem } from "ui/Scripts/source/models/whistleBlow/whistleBlowListItem";
import { ModifyUser } from "../../../../../../QTeamSystems.QTeamIntra.Ui/Scripts/source/models/user/modifyUser";
import { AppUtils } from "../../../framework/appUtils";
import { infiniteListPageSize } from "../../../models/common/webConsts";
import * as whistleBlowService from "../../../models/services/whistleBlowService";
import { ISessionState } from "../../../models/session/sessionState";
import { AddButton } from "../../framework/Button/AddButton";
import { GridRow } from "../../framework/Grid/GridRow/GridRow";
import { IListUsingComponentState, List } from "../../framework/List/List";
import { ListActionButtons } from "../../framework/List/ListActionButtons";
import { ListCardField } from "../../framework/List/ListCardField";
import { MainListContainer } from "source/components/framework/List/MainListContainer";
import { ListDetailsButton } from "../../framework/List/ListDetailsButton";
import { ListLinkValue } from "../../framework/List/ListLinkValue";
import { ListToggleButtons } from "../../framework/List/ListToggleButtons";
import { PageListActionContainer } from "../../framework/List/PageListActionContainer";
import { PageTitle } from "../../framework/Page/PageTitle";
import { ClipTypography } from "../../framework/Typography/ClipTypography";
import { WhistleBlowEditDialog } from "../WhistleBlowEditDialog";
import { WhistleBlowNewDialog } from "../WhistleBlowNewDialog";
import { IWhistleBlowGeneralFilter, IWhistleBlowPageFilterState, WhistleBlowPageDashboardChart } from "../WhistleBlowPageDashboardChart";
import { AppMoreIcon } from "source/components/framework/Icon/AppMoreIcon";

/* eslint-disable no-unused-vars */
export const enum WhistleBlowEditViewMode {
    Informant = "1",
    Company = "2"
}
/* eslint-enable no-unused-vars */

// WhistleBlowListStateCol
export interface IWhistleBlowListStateColProp {
    item: IWhistleBlowListItem;
}

const getStateChipColor = (state: WhistleBlowState) => {
    switch (state) {
    case WhistleBlowState.New:
        return "#FFEEED";
    case WhistleBlowState.Received:
        return "#FFFAE2";
    case WhistleBlowState.InProgress:
        return "#F1F7FF";
    case WhistleBlowState.Resolved:
        return "#E0FAEF";
    case WhistleBlowState.Archived:
    case WhistleBlowState.Archived1:
    case WhistleBlowState.Archived2:
    case WhistleBlowState.Archived3:
        return "rgba(0, 0, 0, 0.08)";
    }
};

const getStateChipIcon = (state: WhistleBlowState) => {
    switch (state) {
    case WhistleBlowState.New:
        return <CircleIcon color="error" sx={{ fontSize: "14px !important" }} />;
    case WhistleBlowState.Received:
        return <MoveToInboxIcon color="warning" sx={{ fontSize: "14px !important" }} />;
    case WhistleBlowState.InProgress:
        return <WatchLaterIcon color="info" sx={{ fontSize: "14px !important" }} />;
    case WhistleBlowState.Resolved:
        return <CheckCircleIcon color="success" sx={{ fontSize: "14px !important" }} />;
    case WhistleBlowState.Archived:
    case WhistleBlowState.Archived1:
    case WhistleBlowState.Archived2:
    case WhistleBlowState.Archived3:
        return <InboxIcon sx={{ fontSize: "14px !important" }} />;
    }
};

export class WhistleBlowListStateCol extends React.Component<IWhistleBlowListStateColProp, {}> {
    render() {
        const item = this.props.item;
        return (
            <Stack direction="row" useFlexGap flexWrap="nowrap" overflow="hidden">
                {!(item.state === WhistleBlowState.Resolved && item.resolvedDate > 0) &&
                    <Chip
                        icon={getStateChipIcon(item.state)}
                        size="small"
                        label={item.stateStr}
                        sx={{
                            backgroundColor: getStateChipColor(item.state),
                            marginRight: ".25rem"
                        }}
                    />
                }
                {item.state === WhistleBlowState.Resolved && item.resolvedDate > 0 &&
                    <Chip
                        icon={getStateChipIcon(item.state)}
                        label={Translations.WhistleBlowStateResolved + " " + Base.timeToDateStr(item.resolvedDate)}
                        size="small"
                        sx={{
                            backgroundColor: getStateChipColor(item.state),
                            marginRight: ".25rem"
                        }}
                    />
                }
                {item.toBeReceivedDate > 0 &&
                    <Tooltip title={String.format(Translations.WhistleBlowMustBeReceivedLatestAtParameter, Base.timeToDateStr(item.toBeReceivedDate))}>
                        <Chip
                            icon={<WatchLaterIcon color="error" sx={{ fontSize: "14px !important" }} />}
                            label={Base.timeToDateStr(item.toBeReceivedDate)}
                            size="small"
                            sx={{ backgroundColor: "#FFEEED", marginRight: ".25rem" }}
                        />
                    </Tooltip>
                }
                {item.toBeRespondedDate > 0 &&
                    <Tooltip title={String.format(Translations.WhistleBlowInformantMustBeNotifiedLatestAtParameter, Base.timeToDateStr(item.toBeRespondedDate))}>
                        <Chip
                            icon={<WatchLaterIcon color="error" sx={{ fontSize: "14px !important" }} />}
                            label={Base.timeToDateStr(item.toBeRespondedDate)}
                            size="small"
                            sx={{ backgroundColor: "#FFEEED", marginRight: ".25rem" }}
                        />
                    </Tooltip>
                }
                {item.unreadMessageCount > 0 &&
                    <Tooltip title={String.format(Translations.UnreadMessagesParameter, item.unreadMessageCount.toString(10))}>
                        <Chip
                            icon={<SmsIcon color="error" sx={{ fontSize: "14px !important" }} />}
                            label={item.unreadMessageCount.toString(10)}
                            size="small"
                            sx={{ backgroundColor: "#FFEEED", marginRight: ".25rem" }}
                        />
                    </Tooltip>
                }
            </Stack>
        );
    }
}

// WhistleBlowListHandlersCol
export interface IWhistleBlowListHandlersColProp {
    item: IWhistleBlowListItem;
}

export class WhistleBlowListHandlersCol extends React.Component<IWhistleBlowListHandlersColProp, {}> {
    render() {
        const item = this.props.item;
        const isArchivedAndCleaned = EnumHelper.isEqual(item.state, WhistleBlowState.Archived) && !item.title;
        if (isArchivedAndCleaned) {
            return (<div>{"-"}</div>);
        }
        const otherHandlers = item.handlers.length > 1 ? item.handlers.slice(1) : [];
        return (
            <Tooltip title={otherHandlers.map(i => i.name).join(", ")}>
                <Box display="flex" alignItems="center" sx={{ overflow: "hidden" }}>
                    {item.handlers.length > 0 &&
                        <ClipTypography>{item.handlers[0].name}</ClipTypography>
                    }
                    {otherHandlers.length > 0 &&
                        <Chip color="info" label={"+" + otherHandlers.length.toString(10)} size="small" sx={{ marginLeft: "8px" }} />
                    }
                </Box>
            </Tooltip>
        );
    }
}

// WhistleBlowList
export interface IWhistleBlowListProp extends IWhistleBlowPageFilterState {
    sessionState: ISessionState;
    filterName: string;
    companyId: string;
    maxAttachmentsTotalSize: number;
    disableGlobalLoading?: boolean;
    listFilterIsSet: boolean;
    units: IIdTitleNode[];
    dataId?: string;
    sortColumn: string;
    sortOrderIsAsc: boolean;
    titleSuffix?: JSX.Element;
    reportTabs?: React.ReactNode;
    onSetSortData: (sortColumn: string, sortOrderIsAsc: boolean) => void;
    onLoadPageData: () => void;
    onArchive: () => void;
    onOpenFiltersDialog: () => void;
}

interface IWhistleBlowListState extends IListUsingComponentState<IWhistleBlowListItem>, IWhistleBlowGeneralFilter {
    // List
    intialized: boolean;
    isLoading: boolean;
    pageSize: number;
    page: number;
    filter: string;

    sortColumn: string;
    sortOrderIsAsc: boolean;
    listModeIsLine: boolean;
    items: IWhistleBlowListItem[];
    checkedIds: string[];

    // New Dialog
    showNewWhistleBlowDialog: boolean;
}

export class WhistleBlowList extends React.Component<IWhistleBlowListProp, IWhistleBlowListState> {
    sortHandlers = (items: IWhistleBlowListItem[]): IWhistleBlowListItem[] => {
        for (const item of items) {
            item.handlers = ModifyUser.sortModifyUsers(item.handlers, { currentUserCode: this.props.sessionState.code });
        }
        return items;
    };

    constructor(props: IWhistleBlowListProp) {
        super(props);
        const generalFilterValues = WhistleBlowPageDashboardChart.getGeneralFilterFromState(props);
        this.state = {
            ...generalFilterValues,
            intialized: false,
            isLoading: false,
            page: 1,
            hasMore: true,
            filter: "",
            pageSize: infiniteListPageSize,
            sortColumn: props.sortColumn,
            sortOrderIsAsc: props.sortOrderIsAsc,
            listModeIsLine: true,
            items: [],
            selectedId: null,
            checkedIds: [],
            // Details
            detailsItem: null,
            // New Dialog
            showNewWhistleBlowDialog: false,
            // Dialog
            showEditDialog: false,
            editDialogId: "",
            editDialogHasPrevious: false,
            editDialogHasNext: false
        };
    }

    searchItems = async (pageSize: number, page: number, filter: string, startDate: number, endDate: number, unitIds: string[], categoryIds: string[], handlerUserCodes: string[], stateFilterId: number, severityFilterId: number, resolutionFilterId: number,
        sourceFilterId: number, anonymityFilterId: number, dynamicTimeFilterId: number, sortColumn: string, sortOrderIsAsc: boolean, resetItems: boolean, refreshList: boolean, moveNext: boolean = false, setDialogData: boolean = true) => {
        this.setState({ isLoading: true });
        const result = await AppUtils.callService(() => whistleBlowService.getWhistleBlowListItems(this.props.companyId, !refreshList ? pageSize : (page * pageSize), !refreshList ? page : 1, filter,
            startDate, endDate, unitIds, categoryIds, handlerUserCodes, stateFilterId, severityFilterId, resolutionFilterId, sourceFilterId, anonymityFilterId, dynamicTimeFilterId, sortColumn, sortOrderIsAsc), false, this.props.disableGlobalLoading, () => this.setState({ isLoading: false }));
        if (!result) return;
        const items = List.getListItems(this.state.items, this.state.selectedId, this.state.checkedIds, this.sortHandlers(result.items), resetItems, refreshList);
        let selectedId = items.selectedId;
        let index = items.items.findIndex(i => i.id === selectedId);
        let detailsItem = index > -1 ? items.items[index] : null;
        if (moveNext && detailsItem && index < items.items.length - 1) {
            index = index + 1;
            detailsItem = items.items[index];
            selectedId = items.items[index].id;
        }
        this.setState({
            intialized: true,
            page: !refreshList ? result.page : page,
            hasMore: result.hasMore,
            filter,
            unitIds,
            categoryIds,
            handlerUserCodes,
            stateFilterId,
            severityFilterId,
            resolutionFilterId,
            sourceFilterId,
            anonymityFilterId,
            dynamicTimeFilterId,
            sortColumn,
            sortOrderIsAsc,
            items: items.items,
            selectedId,
            checkedIds: items.checkedIds,
            detailsItem,
            editDialogId: setDialogData ? (moveNext && detailsItem ? detailsItem.id : "") : this.state.editDialogId,
            editDialogHasPrevious: setDialogData ? index > 0 : this.state.editDialogHasPrevious,
            editDialogHasNext: setDialogData ? (index < items.items.length - 1 || result.hasMore) : this.state.editDialogHasNext,
        });
        this.props.onSetSortData(sortColumn, sortOrderIsAsc);
    };

    refreshList = async (setDialogData: boolean = true) => {
        const state = this.state;
        await this.searchItems(state.pageSize, state.page, state.filter, state.startDate, state.endDate, state.unitIds, state.categoryIds, state.handlerUserCodes, state.stateFilterId,
            state.severityFilterId, state.resolutionFilterId, state.sourceFilterId, state.anonymityFilterId, state.dynamicTimeFilterId, state.sortColumn, state.sortOrderIsAsc, false, true, false, setDialogData);
    };

    componentDidMount(): void {
        const state = this.state;
        this.searchItems(state.pageSize, state.page, state.filter, state.startDate, state.endDate, state.unitIds, state.categoryIds, state.handlerUserCodes, state.stateFilterId,
            state.severityFilterId, state.resolutionFilterId, state.sourceFilterId, state.anonymityFilterId, state.dynamicTimeFilterId, state.sortColumn, state.sortOrderIsAsc, true, false);
    }

    componentDidUpdate(prevProps: IWhistleBlowListProp, prevState: IWhistleBlowListState) {
        const props = this.props;
        const state = this.state;
        const prevDataId = prevProps.dataId ? prevProps.dataId : "";
        const dataId = props.dataId ? props.dataId : "";
        const prevCompanyId = prevProps.companyId ? prevProps.companyId : "";
        const companyId = props.companyId ? props.companyId : "";
        const filterStateChange = WhistleBlowPageDashboardChart.getWhistleBlowGeneralFilterChange(prevProps, props);
        const prevSortColumn = prevProps.sortColumn;
        const sortColumn = props.sortColumn;
        const prevSortOrderIsAsc = prevProps.sortOrderIsAsc;
        const sortOrderIsAsc = props.sortOrderIsAsc;
        if (dataId === prevDataId && companyId === prevCompanyId &&
            filterStateChange.notChanged &&
            prevSortColumn === sortColumn && prevSortOrderIsAsc === sortOrderIsAsc) return;
        this.searchItems(state.pageSize, state.page, state.filter, filterStateChange.startDate, filterStateChange.endDate, filterStateChange.unitIds, filterStateChange.categoryIds, filterStateChange.handlerUserCodes,
            filterStateChange.stateFilterId, filterStateChange.severityFilterId, filterStateChange.resolutionFilterId, filterStateChange.sourceFilterId, filterStateChange.anonymityFilterId,
            filterStateChange.dynamicTimeFilterId, sortColumn, sortOrderIsAsc, true, false);
    }

    changeFilter = (filter: string) => {
        const state = this.state;
        this.searchItems(state.pageSize, 1, filter, state.startDate, state.endDate, state.unitIds, state.categoryIds, state.handlerUserCodes, state.stateFilterId,
            state.severityFilterId, state.resolutionFilterId, state.sourceFilterId, state.anonymityFilterId, state.dynamicTimeFilterId, state.sortColumn, state.sortOrderIsAsc, true, false);
    };

    handleSearchLines = (sortColumn: string, sortOrderIsAsc: boolean, nextPage: boolean) => {
        const state = this.state;
        this.searchItems(state.pageSize, !nextPage ? 1 : state.page + 1, state.filter, state.startDate, state.endDate, state.unitIds, state.categoryIds, state.handlerUserCodes, state.stateFilterId,
            state.severityFilterId, state.resolutionFilterId, state.sourceFilterId, state.anonymityFilterId, state.dynamicTimeFilterId, sortColumn, sortOrderIsAsc, !nextPage, false);
    };

    handleSetSelectedIds = (selectedId: string, checkedIds: string[]) => {
        this.setState({
            selectedId,
            checkedIds,
            detailsItem: this.state.items.find(i => i.id === selectedId)
        });
    };
    
    handleListModeToggle = (_event: React.MouseEvent<HTMLElement>, listModeIsLine: boolean) => {
        this.setState({
            listModeIsLine,
        });
    };

    // #region Edit dialog
    handleOpenLine = (id: string) => {
        this.setState(List.getShowEditDialogStateChange(this.state, id));
    };

    handleWhistleBlowDialogOk = () => {
        this.refreshList(false);
        this.props.onLoadPageData();
    };

    handleWhistleBlowDialogCancel = () => {
        this.setState({
            showEditDialog: false
        });
        this.refreshList();
        this.props.onLoadPageData();
    };

    setEditDialogItemByIndex = (index: number) => {
        const state = this.state;
        this.setState(List.getEditDialogItemByIndexStateChange(this.state, index, () => this.searchItems(state.pageSize, state.page + 1, state.filter, state.startDate, state.endDate, state.unitIds, state.categoryIds, state.handlerUserCodes, state.stateFilterId,
            state.severityFilterId, state.resolutionFilterId, state.sourceFilterId, state.anonymityFilterId, state.dynamicTimeFilterId, state.sortColumn, state.sortOrderIsAsc, false, false, true)));
    };
    // #endregion Edit dialog

    // #region Add dialog
    handleAdd = () => {
        this.setState({
            showNewWhistleBlowDialog: true
        });
    };

    handleEditNewRecordDialogOk = (whistleBlowId: string) => {
        this.setState({
            selectedId: whistleBlowId,
            showNewWhistleBlowDialog: false
        });
        this.refreshList();
    };

    handleEditNewRecordDialogCancel = () => {
        this.setState({
            showNewWhistleBlowDialog: false
        });
    };
    // #endregion Add dialog

    handlePrint = async () => {
        const state = this.state;
        if (!state.selectedId) return;
        const fileName = await AppUtils.callService(() => whistleBlowService.getWhistleblowPrint(state.selectedId));
        if (!fileName) return;
        AppUtils.showSuccessMessage(String.format(Translations.PrintParameterDownloaded, fileName));
    };

    // #region Additional functions: ArchiveWhistleBlows
    handleArchiveWhistleBlows = () => {
        this.props.onArchive();
    };
    // #endregion Additional functions: ArchiveWhistleBlows

    render() {
        const props = this.props;
        const state = this.state;
        return (
            <>
                <PageTitle title={Translations.WhistleBlow + (props.filterName ? " - " + props.filterName : (props.listFilterIsSet || state.filter ? " - " + Translations.Filtered : ""))}>
                    <AddButton onClick={this.handleAdd}>
                        {Translations.AddWhistleBlow}
                    </AddButton>
                </PageTitle>
                <MainListContainer>
                    <PageListActionContainer
                        leftItems={
                            props.reportTabs
                        }
                        rightItems={
                            <>
                                <ListActionButtons
                                    printDisabled={!state.selectedId}
                                    filterIsSet={props.listFilterIsSet}
                                    onFilter={props.onOpenFiltersDialog}
                                    onPrint={this.handlePrint}
                                    onRefresh={() => this.changeFilter("")}
                                />
                                <ListToggleButtons value={state.listModeIsLine} onChange={this.handleListModeToggle} />
                                <ToolButtonDropdown
                                    variant="icon"
                                    actions={[
                                        { title: Translations.ArchiveWhistleBlows, onClick: this.handleArchiveWhistleBlows },
                                    ]}
                                    icon={<AppMoreIcon />}
                                    tooltip={Translations.AdditionalFunctions}
                                    menuRight={true}
                                />
                            </>
                        }
                    />
                    <List
                        intialized={state.intialized}
                        isLoading={state.isLoading}
                        hasMore={state.hasMore}
                        globalLoadingInUse={!props.disableGlobalLoading}
                        height="calc(100vh - 240px)" //Main List
                        header={state.listModeIsLine
                            ? {
                                columns: [
                                    { size: 1, column: "unitCode", content: Translations.Unit },
                                    { size: 1, column: "code", content: Translations.Code },
                                    { size: 1, column: "date", content: Translations.WhistleBlowDateAndTime },
                                    { size: 2, column: "", content: Translations.Title },
                                    { size: 2, column: "state", content: Translations.State },
                                    { size: 2, column: "", content: Translations.Handlers },
                                    { size: 2, column: "category", content: Translations.Category },
                                    { size: 1, column: "severity", content: Translations.Severity }
                                ]
                            }
                            : null}
                        lines={state.listModeIsLine
                            ? state.items.map((item) => {
                                return {
                                    columns: [
                                        { size: 1, content: item.unitCode },
                                        {
                                            size: 1,
                                            content:
                                            <Box display="flex" alignItems="center" onClick={() => this.handleOpenLine(item.id)} sx={{ overflow: "hidden" }}>
                                                <Tooltip title={item.code}>
                                                    <ListLinkValue>{item.code}</ListLinkValue>
                                                </Tooltip>
                                                {!item.anonymous &&
                                                    <Tooltip title={Translations.WhistleBlowNonAnonymousReport}>
                                                        <AccountCircleIcon sx={{ marginLeft: ".25rem" }} fontSize="small" />
                                                    </Tooltip>
                                                }
                                            </Box>
                                        },
                                        { size: 1, content: Base.timeToDateTimeStr(item.date) },
                                        { size: 2, content: item.title ? item.title : "-" },
                                        {
                                            size: 2,
                                            content: <WhistleBlowListStateCol item={item} />
                                        },
                                        {
                                            size: 2,
                                            content: <WhistleBlowListHandlersCol item={item} />
                                        },
                                        { size: 2, content: item.categoryCode },
                                        { size: 1, content: item.severityStr }
                                    ],
                                    id: item.id
                                };
                            })
                            : null
                        }
                        cards={!state.listModeIsLine
                            ? state.items.map((item) => {
                                return {
                                    sm: 4,
                                    rows: [
                                        {
                                            cols: [
                                                {
                                                    size: 6,
                                                    title: Translations.Code,
                                                    value:
                                                        <Box display="flex" alignItems="center" onClick={() => this.handleOpenLine(item.id)}>
                                                            <ListLinkValue>{item.code}</ListLinkValue>
                                                            {item.unreadMessageCount
                                                                ? <MessageIcon sx={{ marginLeft: ".25rem" }} fontSize="small" />
                                                                : null
                                                            }
                                                            {!item.anonymous
                                                                ? <Tooltip title={Translations.WhistleBlowNonAnonymousReport}>
                                                                    <AccountCircleIcon sx={{ marginLeft: ".25rem" }} fontSize="small" />
                                                                </Tooltip>
                                                                : null
                                                            }
                                                        </Box>
                                                },
                                                { size: 6, title: Translations.Date, value: Base.timeToDateStr(item.date) }
                                            ]
                                        },
                                        {
                                            cols: [
                                                { title: Translations.Title, value: item.title },
                                            ]
                                        },
                                        {
                                            cols: [
                                                { size: 6, title: Translations.State, value: <WhistleBlowListStateCol item={item} /> },
                                                { size: 6, title: Translations.Unit, value: item.unitCode }
                                            ]
                                        },
                                        {
                                            cols: [
                                                { size: 6, title: Translations.Category, value: item.categoryCode },
                                                { size: 6, title: Translations.Severity, value: item.severityStr },
                                            ]
                                        }],
                                    id: item.id
                                };
                            })
                            : null
                        }
                        lineDetails={state.detailsItem
                            ? <Box position="relative">
                                <GridRow>
                                    <Grid item sm={4}>
                                        <GridRow>
                                            <Grid item sm={6}>
                                                <ListCardField title={Translations.Code} value={state.detailsItem.code} />
                                            </Grid>
                                            <Grid item sm={6}>
                                                <ListCardField title={Translations.Date} value={Base.timeToDateStr(state.detailsItem.date)} />
                                            </Grid>
                                        </GridRow>
                                        <GridRow>
                                            <Grid item sm={12}>
                                                <ListCardField title={Translations.Title} value={state.detailsItem.title} />
                                            </Grid>
                                        </GridRow>
                                    </Grid>
                                    <Grid item sm={4}>
                                        <GridRow>
                                            <Grid item sm={12}>
                                                <ListCardField title={Translations.State} value={state.detailsItem.stateStr} />
                                            </Grid>
                                        </GridRow>
                                        <GridRow>
                                            <Grid item sm={12}>
                                                <ListCardField title={Translations.Category} value={state.detailsItem.categoryCode} />
                                            </Grid>
                                        </GridRow>
                                    </Grid>
                                    <Grid item sm={4}>
                                        <GridRow>
                                            <Grid item sm={12}>
                                                <ListCardField title={Translations.Severity} value={state.detailsItem.severityStr} />
                                            </Grid>
                                        </GridRow>
                                    </Grid>
                                </GridRow>
                                <ListDetailsButton
                                    onClick={() => this.handleOpenLine(state.selectedId)}
                                />
                            </Box>
                            : null}
                        sortColumn={state.sortColumn}
                        sortOrderIsAsc={state.sortOrderIsAsc}
                        selectedId={state.selectedId}
                        checkedIds={state.checkedIds}
                        onSetSelectedIds={this.handleSetSelectedIds}
                        onSearchLines={this.handleSearchLines}
                        onOpenLine={this.handleOpenLine}
                    />
                    {state.showNewWhistleBlowDialog &&
                    <WhistleBlowNewDialog
                        sessionState={props.sessionState}
                        onOk={this.handleEditNewRecordDialogOk}
                        onCancel={this.handleEditNewRecordDialogCancel}
                    />
                    }
                    {(state.showEditDialog && !!state.editDialogId) &&
                    <WhistleBlowEditDialog
                        sessionState={props.sessionState}
                        maxAttachmentsTotalSize={props.maxAttachmentsTotalSize}
                        whistleBlowId={state.editDialogId}
                        hasPrevious={state.editDialogHasPrevious}
                        hasNext={state.editDialogHasNext}
                        onSaved={this.handleWhistleBlowDialogOk}
                        onClose={this.handleWhistleBlowDialogCancel}
                        onRefreshList={() => this.refreshList(false)}
                        onPrevious={() => List.moveToPrevious(state, this.setEditDialogItemByIndex)}
                        onNext={() => List.moveToNext(state, this.setEditDialogItemByIndex)}
                    />
                    }
                </MainListContainer>
            </>
        );
    }
}